import { useState, useEffect } from 'react';
import { useProfile } from '../hooks/useProfile';
import { useAuth } from '../hooks/useAuth';

export default function Profile() {
  const { user } = useAuth();
  const { profile, isLoading, error, update } = useProfile();
  const [form, setForm] = useState({
    phone: '',
    signatureCs: '',
    signatureEn: '',
    signaturePl: '',
    signatureVi: '',
  });
  const [saved, setSaved] = useState(false);

  useEffect(() => {
    if (profile) {
      setForm({
        phone: profile.phone ?? '',
        signatureCs: profile.signatureCs ?? '',
        signatureEn: profile.signatureEn ?? '',
        signaturePl: profile.signaturePl ?? '',
        signatureVi: profile.signatureVi ?? '',
      });
    }
  }, [profile]);

  const onSave = async () => {
    await update.mutateAsync({
      phone: form.phone.trim() || null,
      signatureCs: form.signatureCs.trim() || null,
      signatureEn: form.signatureEn.trim() || null,
      signaturePl: form.signaturePl.trim() || null,
      signatureVi: form.signatureVi.trim() || null,
    });
    setSaved(true);
    setTimeout(() => setSaved(false), 2000);
  };

  if (isLoading) {
    return (
      <div className="flex justify-center py-16">
        <div className="h-8 w-8 animate-spin rounded-full border-4 border-teal/20 border-t-teal" />
      </div>
    );
  }

  if (error) {
    return <div className="card p-8 text-danger">Chyba: {(error as Error).message}</div>;
  }

  const requiredIncomplete = !form.signatureCs.trim() || !form.signatureEn.trim();
  const userName = user?.name ?? 'Pavlína';

  return (
    <div className="mx-auto max-w-xl">
      <h1 className="mb-2 font-heading text-2xl font-semibold text-dark">Můj profil</h1>
      <p className="mb-6 text-sm text-gray-600">
        Tato data se použijí pro personalizaci broadcast zpráv. CZ + EN podpis je
        povinný, PL a VI volitelné — vyplň jen ty, ve kterých chceš reálně psát
        klientům. Newsletter (z archivu) tyhle podpisy nepoužívá, je vždy podepsán
        „Tým BuyDubai".
      </p>

      {requiredIncomplete && (
        <div className="mb-4 rounded-md border border-warning/40 bg-warning-bg p-3 text-sm text-warning">
          ⚠ Profil není kompletní — doplň aspoň CZ a EN podpis, jinak FeedCard
          broadcasty ukáží chybu „PROFILE_INCOMPLETE".
        </div>
      )}

      <div className="card space-y-4 p-6">
        <div>
          <label className="label">Jméno (z Google, read-only)</label>
          <input className="input bg-gray-50" value={user?.name ?? ''} disabled />
        </div>

        <div>
          <label className="label">Email (read-only)</label>
          <input className="input bg-gray-50" value={user?.email ?? ''} disabled />
        </div>

        <div>
          <label className="label">Telefon (volitelné, pro podpis)</label>
          <input
            className="input"
            value={form.phone}
            onChange={(e) => setForm({ ...form, phone: e.target.value })}
            placeholder="+420 ..."
          />
        </div>

        <div className="grid grid-cols-1 gap-4 md:grid-cols-2">
          <SignatureField
            label="🇨🇿 Podpis CZ"
            required
            value={form.signatureCs}
            onChange={(v) => setForm({ ...form, signatureCs: v })}
            placeholder={`Vaše makléřka,\n${userName}\n+420 ...`}
          />
          <SignatureField
            label="🇬🇧 Podpis EN"
            required
            value={form.signatureEn}
            onChange={(v) => setForm({ ...form, signatureEn: v })}
            placeholder={`Your agent,\n${userName}\n+420 ...`}
          />
          <SignatureField
            label="🇵🇱 Podpis PL"
            value={form.signaturePl}
            onChange={(v) => setForm({ ...form, signaturePl: v })}
            placeholder={`Państwa pośrednik,\n${userName}\n+420 ...`}
            hint="Vyplň pokud chceš generovat broadcasty v polštině."
          />
          <SignatureField
            label="🇻🇳 Podpis VI"
            value={form.signatureVi}
            onChange={(v) => setForm({ ...form, signatureVi: v })}
            placeholder={`Người môi giới của bạn,\n${userName}\n+420 ...`}
            hint="Vyplň pokud chceš generovat broadcasty ve vietnamštině."
          />
        </div>

        <div className="flex items-center justify-end gap-3 pt-2">
          {saved && <span className="text-sm text-positive">✓ Uloženo</span>}
          <button onClick={onSave} className="btn-primary" disabled={update.isPending}>
            {update.isPending ? 'Ukládám…' : 'Uložit'}
          </button>
        </div>
      </div>
    </div>
  );
}

function SignatureField({
  label,
  required,
  value,
  onChange,
  placeholder,
  hint,
}: {
  label: string;
  required?: boolean;
  value: string;
  onChange: (v: string) => void;
  placeholder?: string;
  hint?: string;
}) {
  return (
    <div>
      <label className="label">
        {label}
        {required && <span className="ml-1 text-danger">*</span>}
      </label>
      <textarea
        className="input min-h-[80px]"
        value={value}
        onChange={(e) => onChange(e.target.value)}
        placeholder={placeholder}
      />
      {hint && <p className="mt-1 text-xs text-gray-500">{hint}</p>}
    </div>
  );
}
